import { Stack, Text, Textarea } from '@mantine/core'
import { StatusMessage } from '../Common/StatusMessage'

export const DESCRIPTION_MAX_LENGTH = 1000

export interface TaskHoldDescriptionProps {
  taskType: string
  value: string
  onChange: (value: string) => void
  tooLong: boolean
  // True when a description has been typed but no hold date picked yet.
  needsHoldDate: boolean
  error?: string | null
}

// Save and Cancel for this editor live at the top of the card, beside the collapse
// icon, so they stay put however long the description grows.
export function TaskHoldDescription({
  taskType,
  value,
  onChange,
  tooLong,
  needsHoldDate,
  error = null,
}: Readonly<TaskHoldDescriptionProps>) {
  return (
    <Stack gap={4}>
      <Text fz="xs">Description</Text>
      <Textarea
        value={value}
        onChange={(event) => onChange(event.currentTarget.value)}
        aria-label={`Description for task ${taskType}`}
        aria-invalid={tooLong}
        // Caps typing, as the Present Position description does. It cannot trim a value
        // that arrives over the limit from MC1, so tooLong still guards the save.
        maxLength={DESCRIPTION_MAX_LENGTH}
        // The design shows a white field here, not the theme's light-blue input fill. In
        // dark mode it takes the same fill as every other input, as white glares there.
        styles={{
          input: {
            backgroundColor: 'light-dark(var(--mantine-color-white), var(--mantine-color-dark-6))',
            minHeight: 110,
            borderColor: tooLong ? 'var(--mantine-color-error)' : undefined,
          },
        }}
      />
      {/* Same counter as the Present Position description: it turns red on the last
          character, where the field stops accepting input. */}
      <Text fz="xs" ta="right" c={value.length >= DESCRIPTION_MAX_LENGTH ? 'var(--mantine-color-error)' : 'text.2'}>
        {value.length} / {DESCRIPTION_MAX_LENGTH} characters
      </Text>
      {tooLong && (
        <StatusMessage status="error" fz="xs">
          Description must be {DESCRIPTION_MAX_LENGTH} characters or fewer
        </StatusMessage>
      )}
      {/* Without this Save would sit greyed out with nothing explaining why, since the
          description looks complete. Coloured like the other validation messages — it is
          a rule standing between the user and Save — but role="status" rather than the
          default alert, because it names a remaining step, not a failure. */}
      {needsHoldDate && (
        <StatusMessage status="error" fz="xs" role="status">
          Set a hold date to save
        </StatusMessage>
      )}
      {error && (
        <StatusMessage status="error" fz="xs">
          {error}
        </StatusMessage>
      )}
    </Stack>
  )
}
